import { createFileRoute, lazyRouteComponent, redirect } from '@tanstack/react-router';
import { z } from 'zod';
import { isForbiddenError, isNotFoundError } from '@/api/errors';
import { spaceService } from '@/api/services/serverless/space';
import { spaceCollectionsService } from '@/api/services/serverless/space-collections';
import { spaceMonitoringService, type SpaceUsageFilter } from '@/api/services/serverless/space-monitoring';
import { safeAsync } from '@/helpers/errors/safeAsync';
import { validateParams } from '@/router/utils';

export const Route = createFileRoute('/_authenticated/_dashboard/accounts/$accountId/serverless-spaces/$spaceId')({
  params: validateParams(
    z.object({
      spaceId: z.guid(),
    }),
  ),
  beforeLoad: async ({ context: { queryClient }, matches, params }) => {
    const { accountId, spaceId } = params;
    const lastMatch = matches.at(-1);

    const spaceQuery = spaceService.getSpaceQuery(params);
    const [spaceError] = await safeAsync(queryClient.fetchQuery(spaceQuery));
    if (spaceError) {
      if (isNotFoundError(spaceError) || isForbiddenError(spaceError)) {
        throw redirect({
          to: '/accounts/$accountId/serverless-spaces',
          params: { accountId },
          replace: true,
        });
      }
      throw spaceError;
    }

    if (lastMatch?.routeId === Route.id) {
      throw redirect({
        to: '/accounts/$accountId/serverless-spaces/$spaceId/overview',
        params,
        replace: true,
      });
    }

    return {
      spaceQuery,
      // Account-wide: the rename and create-from-backup dialogs check names against every space.
      listSpacesQuery: spaceService.listSpacesQuery({ accountId }),
      // Per endpoint URL: the space can get one while a tab is open.
      listCollectionsQuery: (baseUrl: string, limit?: number) =>
        spaceCollectionsService.listCollectionsQuery({ accountId, spaceId, baseUrl, limit }),
      getCollectionQuery: (baseUrl: string, collectionName: string) =>
        spaceCollectionsService.getCollectionQuery({ accountId, spaceId, baseUrl, collectionName }),
      // The overview and metrics views pick the range; a collection's metrics view narrows it to that collection.
      spaceUsageMetricsQuery: (filter: SpaceUsageFilter) =>
        spaceMonitoringService.getSpaceUsageMetricsQuery({ accountId, spaceId, ...filter }),
    };
  },
  // Not awaited: usually warm from the spaces list, and only the dialogs read it.
  loader: ({ context: { queryClient, listSpacesQuery } }) => void queryClient.prefetchQuery(listSpacesQuery),
  pendingComponent: lazyRouteComponent(
    () => import('@/components/Serverless/SpaceLayout'),
    'SpaceLayoutPendingComponent',
  ),
});
